import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { IoIosSearch } from 'react-icons/io';
import { IoSettingsOutline } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import { MdArrowBackIosNew } from 'react-icons/md';
import ApiScaleGridMultiprofissional from './ApiScaleGridMultiprofissional';
import SearchableSetorSelect from '../Escalas Utils/SearchableSetorSelect';
import {
	requestBuscarGradeEscalaMultiprofissional,
} from '../../services/escalaMultiprofissionalRequests';
import { requestFiltrosBuscaEscalaMultiprofissional } from '../../services/escalaMultiprofissionalRequests';
import { sortSetoresPorAndar } from '../../utils/escalaSetorOrdering';
import { canManageEscalaMultiprofissional, isDeveloperUser } from '../../utils/escalaFeatures';

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
	value: String(index + 1).padStart(2, '0'),
	label: new Date(2026, index, 1).toLocaleDateString('pt-BR', { month: 'long' }),
}));

const getErrorMessage = (result, fallback) => {
	if (!result?.error) return '';
	const issues = result.error.issues || [];
	const issueText = issues
		.map((issue) => String(issue?.mensagem || issue?.message || '').trim())
		.filter(Boolean)
		.join(' ');
	return issueText || result.error.message || fallback;
};

const normalizeUnitSearch = (value = '') =>
	String(value)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const formatUnidadeOptionLabel = (unidade = {}) => {
	const nome = String(unidade?.nome || '').trim();
	return normalizeUnitSearch(nome) === normalizeUnitSearch('CER Jacarepaguá')
		? 'Hospital Cardoso Fontes'
		: nome;
};

const normalizeUnits = (payload) => {
	const list = Array.isArray(payload)
		? payload
		: Array.isArray(payload?.results)
			? payload.results
			: Array.isArray(payload?.estabelecimentos)
				? payload.estabelecimentos
				: Array.isArray(payload?.unidades)
					? payload.unidades
			: [];
	const map = new Map();
	list.forEach((item) => {
		const cnes = String(
			item?.cnes || item?.cnes_estabelecimento || item?.id || item?.codigo || item?.value || '',
		).trim();
		const nome = String(
			item?.nome || item?.nome_fantasia || item?.unidade || item?.label || '',
		).trim();
		if (!cnes || !nome || normalizeUnitSearch(nome) === 'cer andarai' || map.has(cnes)) return;
		map.set(cnes, { cnes, nome });
	});
	return Array.from(map.values());
};

const normalizeOption = (item = {}) => ({
	value: String(item.id || item.codigo || item.value || item.identificador || ''),
	label: String(item.nome_exibicao || item.nome || item.label || item.descricao || item.identificador || item.id || ''),
});

const getFilterSectors = (payload = {}) => {
	if (Array.isArray(payload?.estrutura?.setores)) return payload.estrutura.setores;
	if (Array.isArray(payload?.setores)) return payload.setores;
	const groups = [
		...(Array.isArray(payload?.tipos_secao) ? payload.tipos_secao : []),
		...(Array.isArray(payload?.servicos) ? payload.servicos : []),
	];
	return groups.flatMap((group) =>
		Array.isArray(group?.setores) ? group.setores : [],
	);
};

const defaultFilters = () => ({
	unidade: '',
	setor: '',
	regime: '',
	mes: String(new Date().getMonth() + 1).padStart(2, '0'),
	ano: String(new Date().getFullYear()),
});

function FormScaleMultiprofissional() {
	const navigate = useNavigate();
	const [token, setToken] = useState('');
	const [user, setUser] = useState(null);
	const [filters, setFilters] = useState(defaultFilters);
	const [units, setUnits] = useState([]);
	const [unitSearchTerm, setUnitSearchTerm] = useState('');
	const [showUnitSuggestions, setShowUnitSuggestions] = useState(false);
	const [sectors, setSectors] = useState([]);
	const [regimes, setRegimes] = useState([]);
	const [loadingFilters, setLoadingFilters] = useState(true);
	const [loadingGrid, setLoadingGrid] = useState(false);
	const [grade, setGrade] = useState(null);
	const [message, setMessage] = useState('Selecione os filtros e clique em Buscar Escala.');
	const [error, setError] = useState('');

	const canSelectUnit = isDeveloperUser(user || {}) ||
		String(user?.unidade || '').toLowerCase() === 'subhue' || user?.is_subhue === true;
	const canManage = canManageEscalaMultiprofissional(user || {});

	const loadFilters = useCallback(async (establishment, initial = false) => {
		if (!token || !establishment) {
			setLoadingFilters(false);
			return;
		}
		setLoadingFilters(true);
		setError('');
		const result = await requestFiltrosBuscaEscalaMultiprofissional({
			token,
			estabelecimento: establishment,
		});
		if (result?.error) {
			setError(getErrorMessage(result, 'Não foi possível carregar os filtros da escala multiprofissional.'));
			setLoadingFilters(false);
			return;
		}

		const nextSectors = sortSetoresPorAndar(getFilterSectors(result))
			.map(normalizeOption)
			.filter((item) => item.value);
		const nextRegimes = (result.regimes || []).map(normalizeOption).filter((item) => item.value);
		setSectors(nextSectors);
		const availableRegimes = nextRegimes.length ? nextRegimes : [
			{ value: 'TODOS', label: 'Todos' },
			{ value: 'PLANTONISTA', label: 'Plantonista' },
			{ value: 'ROTINA', label: 'Rotina' },
		];
		setRegimes(availableRegimes);
		setFilters((current) => ({
			...current,
			regime: availableRegimes.some((item) => String(item.value) === String(current.regime))
				? current.regime
				: availableRegimes.find((item) => String(item.value).toUpperCase() === 'TODOS')?.value || availableRegimes[0]?.value || '',
		}));
		const defaultCompetence = result.competenciaPadrao || result.competencia_padrao;
		if (initial && defaultCompetence) {
			setFilters((current) => ({
				...current,
				mes: String(defaultCompetence.mes || current.mes).padStart(2, '0'),
				ano: String(defaultCompetence.ano || current.ano),
			}));
		}
		setLoadingFilters(false);
	}, [token]);

	const loadGenericUnits = useCallback(async (currentEstablishment, currentUser) => {
		const result = await requestFiltrosBuscaEscalaMultiprofissional({
			token: currentUser?.token,
			somenteComItens: true,
		});
		if (result?.error) return;
		const normalizedUnits = normalizeUnits(result);
		if (
			currentEstablishment &&
			!normalizedUnits.some((item) => item.cnes === currentEstablishment)
		) {
			normalizedUnits.unshift({
				cnes: currentEstablishment,
				nome: String(currentUser?.unidade || currentEstablishment),
			});
		}
		setUnits(normalizedUnits);
	}, []);

	useEffect(() => {
		const rawUser = Cookies.get('user');
		if (!rawUser) {
			setError('Usuário não autenticado para acessar a escala multiprofissional.');
			setLoadingFilters(false);
			return;
		}
		try {
			const parsedUser = JSON.parse(rawUser);
			const establishment = String(parsedUser.cnes || parsedUser.cnes_estabelecimento || parsedUser.cnesEstabelecimento || '').trim();
			const canChooseUnit =
				isDeveloperUser(parsedUser) ||
				String(parsedUser?.unidade || '').toLowerCase() === 'subhue' ||
				parsedUser?.is_subhue === true;
			setUser(parsedUser);
			setToken(parsedUser.token || '');
			setUnitSearchTerm('');
			setFilters((current) => ({
				...current,
				unidade: canChooseUnit ? '' : establishment,
			}));
			if (canChooseUnit) setLoadingFilters(false);
			loadGenericUnits(establishment, parsedUser);
		} catch (_error) {
			setError('Não foi possível ler os dados do usuário autenticado.');
			setLoadingFilters(false);
		}
	}, [loadGenericUnits]);

	useEffect(() => {
		if (token && filters.unidade) loadFilters(filters.unidade, true);
	}, [filters.unidade, loadFilters, token]);

	const handleChange = (field, value) => {
		setFilters((current) => ({
			...current,
			[field]: value,
			...(field === 'unidade' ? { setor: '', regime: '' } : {}),
		}));
		if (field === 'unidade') {
			setSectors([]);
			setRegimes([]);
			setGrade(null);
		}
	};

	const filteredUnits = useMemo(() => {
		const term = normalizeUnitSearch(unitSearchTerm);
		if (!term) return units;
		return units.filter((unit) => {
			const nome = normalizeUnitSearch(formatUnidadeOptionLabel(unit));
			const cnes = normalizeUnitSearch(unit.cnes || '');
			return nome.includes(term) || cnes.includes(term);
		});
	}, [unitSearchTerm, units]);

	const handleUnitInputChange = (value) => {
		setUnitSearchTerm(value);
		setShowUnitSuggestions(true);
		const input = normalizeUnitSearch(value);
		const matchingUnit = units.find((unit) => {
			const label = normalizeUnitSearch(formatUnidadeOptionLabel(unit));
			const cnes = normalizeUnitSearch(unit.cnes || '');
			return label === input || cnes === input;
		});
		handleChange('unidade', matchingUnit ? String(matchingUnit.cnes) : '');
	};

	const handleSelectUnit = (unit) => {
		setUnitSearchTerm(formatUnidadeOptionLabel(unit));
		setShowUnitSuggestions(false);
		handleChange('unidade', String(unit.cnes));
	};

	const isSectorDisabled =
		loadingFilters || (canSelectUnit && !filters.unidade);
	const isRegimeDisabled =
		loadingFilters || (canSelectUnit && !filters.unidade);

	const handleSearch = async () => {
		if (!filters.unidade || !filters.setor) {
			setError('Selecione uma unidade e um setor para consultar a escala.');
			return;
		}
		setLoadingGrid(true);
		setError('');
		const result = await requestBuscarGradeEscalaMultiprofissional({
			token,
			estabelecimento: filters.unidade,
			setor: filters.setor,
			regime: filters.regime,
			mes: Number(filters.mes),
			ano: Number(filters.ano),
		});
		if (result?.error) {
			setGrade(null);
			setError(getErrorMessage(result, 'Não foi possível buscar a grade multiprofissional.'));
		} else {
			setGrade(result);
			setMessage('Nenhum item foi encontrado para os filtros selecionados.');
		}
		setLoadingGrid(false);
	};

	const yearOptions = useMemo(() => Array.from({ length: 5 }, (_, index) => String(new Date().getFullYear() - 1 + index)), []);

	return (
		<div className='flex flex-col w-full min-h-screen'>
			<div className='text-center flex-grow mt-[64px] sm:mt-[80px]'>
				<div className='flex flex-col w-full mx-auto text-center flex-grow bg-[#F0F4F8]'>
					<div className='mx-[7%] flex justify-center text-[#13335A] font-semibold text-[26px] sm:text-[28px] py-[28px] sm:py-[36px]'>Escala Multiprofissional</div>
				</div>
				<div className='flex my-[18px] flex-col items-center w-full mx-auto rounded-md mb-8 px-2 sm:px-0'>
					<div className='w-[90%] sm:w-[95%] flex justify-start max-w-[1600px]'>
						<Link to='/escala' className='inline-flex items-center gap-1.5 text-[18px] font-medium text-[#17395f] hover:text-[#60758f] duration-300 transition-colors mb-4'><MdArrowBackIosNew size={15} />Voltar</Link>
					</div>
					<div className='w-[90%] sm:w-[95%] flex flex-col gap-2 border border-gray-100 rounded-lg shadow-md p-5 max-w-[1600px] bg-white'>
						<p className='text-[#13335A] font-medium text-[24px] text-start w-full'>Filtros de pesquisa</p>
						<hr className='w-full border-t border-gray-200 my-2' />
						{canSelectUnit && (
							<div className='flex flex-col w-full text-left'>
								<label className='font-medium text-[18px] text-[#13335A]'>Unidade</label>
								<div className='relative'>
									<input
										type='text'
										value={unitSearchTerm}
										onChange={(event) => handleUnitInputChange(event.target.value)}
										onFocus={() => setShowUnitSuggestions(true)}
										onBlur={() => setTimeout(() => setShowUnitSuggestions(false), 120)}
										placeholder='Selecione uma unidade'
										className='border border-gray-300 rounded-lg px-3 py-2 mt-1 w-full font-semibold text-[#949494] outline-none'
									/>
									{showUnitSuggestions && (
										<div className='absolute left-0 right-0 top-full mt-1 z-30 w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-sm'>
											{filteredUnits.length > 0 ? (
												filteredUnits.map((unit) => (
													<button type='button' key={unit.cnes} onMouseDown={(event) => { event.preventDefault(); handleSelectUnit(unit); }} className='block w-full px-3 py-2 text-left text-[14px] text-[#4F5257] hover:bg-[#F3F4F6]'>
														{formatUnidadeOptionLabel(unit)}
													</button>
												))
											) : (
												<div className='px-3 py-2 text-[13px] text-[#9CA3AF]'>Nenhuma unidade encontrada</div>
											)}
										</div>
									)}
								</div>
							</div>
						)}
						<div className='flex flex-col md:flex-row justify-between items-end gap-4 md:gap-[32px] w-full text-left'>
							<div className='flex flex-col w-full min-w-0'><label className='font-medium text-[18px] text-start text-[#13335A]'>Setor</label><SearchableSetorSelect value={filters.setor} options={sectors} onChange={(value) => handleChange('setor', value)} disabled={isSectorDisabled} placeholder={canSelectUnit && !filters.unidade ? 'Selecione primeiro a unidade' : loadingFilters ? 'Carregando...' : 'Selecione o setor'} emptyMessage='Nenhum setor cadastrado para esta unidade' className='mt-1 rounded-lg border border-gray-300 px-3 py-2 font-semibold text-[#949494] outline-none disabled:bg-gray-100' /></div>
							<div className='flex flex-col w-full'><label className='font-medium text-[18px] text-[#13335A]'>Regime</label><select value={filters.regime} disabled={isRegimeDisabled} onChange={(event) => handleChange('regime', event.target.value)} className='border border-gray-300 rounded-lg px-3 py-2 mt-1 w-full font-semibold text-[#949494] appearance-none disabled:cursor-not-allowed disabled:bg-gray-100'>{!filters.unidade && <option value=''>Selecione primeiro a unidade</option>}{regimes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div>
							<div className='bg-white w-full md:w-auto md:min-w-[320px] rounded-lg'><p className='text-[16px] text-[#13335A] font-medium mb-2 text-start'>Data vigente</p><div className='flex gap-2'><div className='flex-1 min-w-[120px]'><label className='font-medium text-[14px] text-[#13335A] block mb-1'>Mês</label><select value={filters.mes} onChange={(event) => handleChange('mes', event.target.value)} className='border border-gray-300 rounded-lg px-3 py-2 w-full text-[#949494]'>{monthOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div><div className='flex-1 min-w-[120px]'><label className='font-medium text-[14px] text-[#13335A] block mb-1'>Ano</label><select value={filters.ano} onChange={(event) => handleChange('ano', event.target.value)} className='border border-gray-300 rounded-lg px-3 py-2 w-full text-[#949494]'>{yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}</select></div></div></div>
						</div>
						<div className='flex justify-center w-full'><button type='button' onClick={handleSearch} disabled={loadingFilters || loadingGrid} className='bg-[#FFFFFF] text-[#13335A] border border-[#ebebeb] text-[16px] mt-5 px-4 py-2 rounded-md hover:bg-[#13335A] hover:text-white hover:border-[#13335A] transition-all duration-300 shadow-md font-inter disabled:opacity-60' style={{ width: '100%', maxWidth: '200px' }}><IoIosSearch className='inline-block mr-2 w-[20px] h-auto' />Buscar Escala</button></div>
					</div>
				</div>
				{canManage && <div className='flex flex-col items-center mb-5'><div className='flex flex-col max-w-[1600px] w-[90%] sm:w-[95%] mt-4 items-center'><button type='button' onClick={() => navigate('/escala-multiprofissional/profissionais')} className='flex w-[190px] sm:w-auto items-center justify-center gap-2 sm:gap-3 bg-[#004A80] text-[#FFFFFF] px-3 py-1.5 text-[13px] sm:px-4 sm:py-2 sm:text-base font-light hover:bg-[#0d466c] transition rounded-[7px] shadow-md'><IoSettingsOutline className='w-[16px] h-[16px] sm:w-[20px] sm:h-[20px]' /><span className='inline-block w-[2px] h-5 sm:h-6 bg-[#003D69]' /><span className='flex items-center h-full font-inter'>Gerenciar Profissionais</span></button></div></div>}
				{error && <div className='mx-auto mb-4 w-[90%] sm:w-[95%] max-w-[1600px] rounded-lg border border-[#F5C2C7] bg-[#FDECEE] px-4 py-3 text-left text-[14px] text-[#A61B29]'>{error}</div>}
				{loadingGrid ? <div className='flex justify-center h-[300px] content-center w-full my-20'><div className='w-8 h-8 rounded-full border-4 border-[#dbeafe] border-t-[#2563eb] animate-spin' /></div> : grade ? <ApiScaleGridMultiprofissional payload={grade} canManageItems={false} /> : <div className='w-full text-center py-14 text-[#757575] text-[18px] font-medium'>{message}</div>}
			</div>
		</div>
	);
}

export default FormScaleMultiprofissional;
